import {
  Button,
  Css,
  IconButton,
  MultiSelectField,
  TestIds,
  Tooltip,
  useComputed,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { fragment, ObjectState } from "@homebound/form-state";
import { observer, Observer } from "mobx-react";
import { ReadyPlanOptionGroupPersonalizationType } from "src/generated/graphqlTypes";
import { PlanEditorHeader } from "src/routes/components/PlanConfiguration/PlanEditorHeader";
import {
  MaterialSelectionResult,
  PlaceholderMaterialSelection,
  StagedProductSearchModal,
} from "src/routes/libraries/design-catalog/components/PlaceholderProductSearch";
import {
  EditPersonalizationOptionWizard_OptionCombinationFragment,
  EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment,
} from "src/routes/libraries/design-catalog/personalization-option/EditPersonalizationOptionWizard.generated";
import { PackageVariantsGridTable } from "src/routes/libraries/design-catalog/personalization-option/components/PackageVariantsGridTable";
import {
  canAssignTargetCombinations,
  FormValue,
  PackageOption,
  PlaceholderColumn,
  removeDuplicateCombinations,
} from "src/routes/libraries/design-catalog/personalization-option/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/routes/libraries/product-catalog/components/product-images-viewer/ProductImageViewer";
import { fail } from "src/utils";

type VariantsStepProps = {
  formState: ObjectState<FormValue>;
  title: string;
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[];
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
};

/** The second step: configure the product variants for the option. */
export function VariantsStep(props: VariantsStepProps) {
  const { formState, title, targetGroups, targetCombinations } = props;
  const tid = useTestIds({});

  return (
    <Observer>
      {() => {
        // Package matrix needs full width (horizontal scroll + sticky cols); product list stays centered.
        const isPackage = formState.type.value === ReadyPlanOptionGroupPersonalizationType.Package;
        return (
          // Fill the stepper area + hide overflow so the table region, not the page, scrolls.
          <div css={Css.bgWhite.fg1.df.fdc.oh.$} {...tid}>
            <PlanEditorHeader title={title} />
            {isPackage ? (
              // Full-width; the grid's own toolbar sits above the table.
              <div css={Css.df.fdc.fg1.oh.mw0.px5.pt3.$}>
                <PackageVariantsSection
                  formState={formState}
                  tid={tid}
                  targetCombinations={targetCombinations}
                  targetGroups={targetGroups}
                />
              </div>
            ) : (
              // Single/linked: full-width, vertically-scrolling card list.
              <div css={Css.df.fdc.gap3.w100.p5.fg1.oya.mw0.$}>
                <h2 css={Css.xl.gray900.$}>Variants</h2>
                <ProductVariantsSection formState={formState} tid={tid} targetCombinations={targetCombinations} />
              </div>
            )}
          </div>
        );
      }}
    </Observer>
  );
}

/** The variants for linked/single product personalization options. */
const ProductVariantsSection = observer(function ProductVariantsSection(props: {
  tid: TestIds;
  formState: ObjectState<FormValue>;
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
}) {
  const { tid, formState, targetCombinations } = props;
  const { openModal } = useModal();
  // Assume we have at least 1 controlled placeholder, and we can use it as our "requirements" for the product search
  const placeholderTliId = formState.placeholders.value.first?.id;
  return (
    <div css={Css.df.fdc.gap2.$}>
      <InlineGrid /* product list */>
        {formState.productOptions.rows.map((productOption) => {
          const mv = productOption.data.value;
          return (
            <div key={mv.id} css={Css.df.fdc.jcc.aifs.gapPx(10).ba.bcGray200.br12.bshBasic.p2.$}>
              <div css={Css.df.aic.gap2.$}>
                <div css={Css.fs0.wPx(120).hPx(120).br8.oh.$}>
                  <img
                    css={Css.w100.h100.br8.objectContain.$}
                    src={mv.featuredImage?.asset?.previewUrl || PRODUCT_FALLBACK_IMG_URL}
                    alt={mv.displayName}
                  />
                </div>
                <div css={Css.df.fdc.jcc.aifs.gapPx(2).hPx(96).fg1.mw0.$}>
                  {mv.listing.brand && (
                    <span css={Css.xs.gray500.$} {...tid.materialBrand}>
                      {mv.listing.brand.name}
                    </span>
                  )}
                  <Tooltip {...tid.materialName} title={mv.displayName} placement="top">
                    <span css={Css.xs.gray900.lineClamp4.$}>{mv.displayName}</span>
                  </Tooltip>
                </div>
              </div>

              {/* Bottom inputs row: the combinations select (when applicable) plus the remove control. */}
              <div css={Css.df.aic.gap1.w100.$}>
                {canAssignTargetCombinations(formState.type.value) && (
                  <div css={Css.fg1.mw0.$}>
                    <MultiSelectField
                      label="Option Combinations"
                      disabled={targetCombinations.isEmpty}
                      options={targetCombinations}
                      placeholder="Select Option Combinations"
                      labelStyle="hidden"
                      onSelect={(val) => {
                        removeDuplicateCombinations(productOption, formState.productOptions.rows, val);
                        productOption.personalizationTargetCombinations.set(
                          val.map((v) => ({ options: v.split(",") })),
                        );
                      }}
                      values={productOption.personalizationTargetCombinations.value.map((c) => c.options.join(","))}
                    />
                  </div>
                )}
                <IconButton
                  icon="remove"
                  disabled={formState.readOnly}
                  onClick={() => formState.productOptions.remove(productOption.value)}
                  {...tid.materialRemove}
                />
              </div>
            </div>
          );
        })}
        {placeholderTliId && (
          <div css={Css.df.mhPx(152).bgGray100.ba.bcGray200.br12.$}>
            <div css={Css.ma.$}>
              <Button
                label="Add Variant"
                variant="secondary"
                icon="plus"
                disabled={formState.readOnly}
                onClick={() =>
                  openModal({
                    content: <ProductSearchModal placeholderTliId={placeholderTliId} formState={formState} />,
                    size: "xxl",
                  })
                }
              />
            </div>
          </div>
        )}
      </InlineGrid>
    </div>
  );
});

/** The variants for package product personalization options, rendered as a `GridTable`. */
const PackageVariantsSection = observer(function PackageVariantsSection(props: {
  tid: TestIds;
  formState: ObjectState<FormValue>;
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[];
}) {
  const { tid, formState, targetCombinations, targetGroups } = props;
  const { openModal } = useModal();

  // The grid derives per-material columns itself; here we just sort the slots into display order.
  const sortedPlaceholderRows = useComputed(
    () => [...formState.placeholders.rows].sortBy((row) => row.order.value ?? Infinity),
    [formState.placeholders],
  );

  return (
    <PackageVariantsGridTable
      formState={formState}
      slots={sortedPlaceholderRows}
      targetCombinations={targetCombinations}
      targetGroups={targetGroups}
      tid={tid}
      onOpenProductSearch={(placeholderTliId, packageOption, column) =>
        openModal({
          content: (
            <ProductSearchModal
              placeholderTliId={placeholderTliId}
              formState={formState}
              packageOption={packageOption}
              column={column}
            />
          ),
          size: "xxl",
        })
      }
    />
  );
});

/**
 * Wraps the `PlaceholderProductSearch` component in a modal.
 *
 * Unlike the existing DP "product search" flow, which is page/route based, we cannot leave the
 * page here b/c the user might have WIP changes to a new/unsaved RPOG.
 */
type ProductSearchModalProps = {
  placeholderTliId: string;
  formState: ObjectState<FormValue>;
  packageOption?: ObjectState<PackageOption>;
  /** The grid column the search was opened from; tells us which (material, location) cell to set. */
  column?: PlaceholderColumn;
};

function ProductSearchModal(props: ProductSearchModalProps) {
  const { placeholderTliId, formState, packageOption, column } = props;
  const currentMaterials = useComputed<PlaceholderMaterialSelection>(() => {
    return { kind: "materials", ids: formState.productOptions.value.map((po) => po.materialVariantId) };
  }, [formState]);

  // Note: for the LinkedProduct/SingleProduct pool, this only ever saves the single staged pick;
  // we could allow staging/saving multiple products at once, but we should wait for user feedback
  // before adding that complexity.
  function saveStagedSelection(selection: MaterialSelectionResult) {
    // mv is only undefined for "noMaterialVariant" selections, which can only happen for Package options
    const mv = selection.kind === "material" ? selection.mv : undefined;
    switch (formState.type.value) {
      case ReadyPlanOptionGroupPersonalizationType.LinkedProduct:
      case ReadyPlanOptionGroupPersonalizationType.SingleProduct:
        // Single/Linked are just a single pool of products, just make sure this one doesn't already exist
        if (mv && !formState.productOptions.value.some((po) => po.materialVariantId === mv.id)) {
          formState.productOptions.add({ materialVariantId: mv.id, data: fragment(mv) });
        }
        break;
      case ReadyPlanOptionGroupPersonalizationType.Package: {
        if (!packageOption) fail("Expecting packageOption prop");
        const placeholder =
          formState.placeholders.rows.find((p) => p.id.value === placeholderTliId) ??
          fail(`Missing placeholder ${placeholderTliId}`);
        const materialId = placeholder.data.value.materialVariant!.id;
        const productMaterial = mv ? { id: mv.id, data: mv } : undefined;
        // Target this column's cell (expanded → its location; collapsed → locationless).
        const cellLocationId = column?.expanded ? column.locationId : undefined;
        const existingProduct = packageOption.products.rows.find(
          (p) => p.placeholderMaterialId.value === materialId && (p.locationId.value ?? undefined) === cellLocationId,
        );
        if (existingProduct) {
          existingProduct.productMaterial.set(productMaterial);
        } else {
          packageOption.products.add({
            placeholderMaterialId: materialId,
            productMaterial,
            locationId: cellLocationId,
          });
        }
        break;
      }
      default:
        break;
    }
  }

  return (
    <StagedProductSearchModal
      placeholderTliId={placeholderTliId}
      showNoMaterialVariant={!!packageOption}
      currentMaterials={currentMaterials}
      onSave={saveStagedSelection}
    />
  );
}

// Figma expresses this as flex-wrap (its auto-layout has no grid primitive), but the design intent is
// uniform, column-aligned cards — which in CSS is a grid. `auto-fill` + `minmax(330px, 1fr)` keeps the
// card's 330px min while making every card (and row, including a partial last row) the same width.
const InlineGrid = ({ children }: React.PropsWithChildren<unknown>) => (
  <div css={Css.dg.gtc("repeat(auto-fill, minmax(330px, 1fr))").gap2.mb4.$}>{children}</div>
);
